/**
 * Правила публичного ника на клиенте (стори 18.2) — порт
 * `backend/src/main/java/com/vrmessenger/handle/PublicHandleUtils.java`.
 *
 * <p>Зачем копия: сервер молча приводит ввод к канонической форме («My-Handle» → `my_handle`) и
 * может отказать по причинам, о которых поле ввода не догадывается. Раньше человек узнавал об
 * этом только после сохранения — и то английской строкой от сервера. Теперь форма показывает
 * будущий ник до нажатия «Сохранить», а причину отказа называет по-русски.
 *
 * <p>Копия — это риск разъехаться с сервером, поэтому:
 * <ul>
 *   <li>правила здесь ровно те же, включая списки, и проверяются теми же кейсами, что и
 *       `PublicHandleUtilsTest`;
 *   <li>последнее слово всё равно за сервером: клиент только предупреждает и подсвечивает,
 *       но не решает за него — сохранение всегда проходит серверную валидацию.
 * </ul>
 */

const HANDLE_BODY = /^[a-z0-9_]{4,32}$/;

/** Совпадает с `PublicHandleUtils.RESERVED`. */
const RESERVED = new Set([
  'admin',
  'administrator',
  'support',
  'help',
  'svyaz',
  'vrmessenger',
  'vr_messenger',
  'system',
  'root',
  'security',
  'api',
  'www',
  'mail',
  'ftp',
  'null',
  'undefined',
  'me',
  'you',
  'mod',
  'moderator',
  'staff',
  'official',
  'channel',
  'group',
  'user',
  'users',
  'chats',
  'messages',
  'settings',
  'login',
  'logout',
  'oauth',
  'static',
  'assets',
  'public',
  'private',
  'c',
  'u',
  'ws',
  'health',
]);

/** Совпадает с `PublicHandleUtils.PROFANITY`: сравнение целиком или по сегментам через `_`. */
const PROFANITY = new Set([
  'fuck',
  'shit',
  'cunt',
  'nigger',
  'nigga',
  'rape',
  'porn',
  'sex',
  'hitler',
  'nazi',
]);

/** Снимает ведущую `#` и пробелы. Ничего не проверяет — как `stripDisplayPrefix` на сервере. */
export function stripHandlePrefix(raw: string | null | undefined): string {
  if (!raw) return '';
  const s = raw.trim();
  return s.startsWith('#') ? s.slice(1).trim() : s;
}

/**
 * Каноническая форма — та, что уйдёт в базу: NFKD без диакритики, нижний регистр, дефисы в
 * подчёркивания, всё остальное вычёркивается, обрезка до 32 с отсечением хвостовых `_`.
 */
export function normalizePublicHandleInput(raw: string | null | undefined): string {
  let s = stripHandlePrefix(raw);
  if (s.length === 0) return '';
  // NFKD + вычёркивание диакритики: «Ünïcodé» → «unicode», как Normalizer.Form.NFKD на сервере.
  s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  s = s.toLowerCase();
  s = s.replace(/-/g, '_');
  s = s.replace(/[^a-z0-9_]/g, '');
  if (s.length > 32) {
    s = s.slice(0, 32).replace(/_+$/, '');
  }
  return s;
}

/** Почему ник не подойдёт. `null` — подойдёт (насколько это видно клиенту). */
type HandleProblem = 'empty' | 'format' | 'reserved' | 'profanity';

export function publicHandleProblem(storageForm: string): HandleProblem | null {
  if (!storageForm || storageForm.trim().length === 0) return 'empty';
  if (!HANDLE_BODY.test(storageForm)) return 'format';
  if (RESERVED.has(storageForm)) return 'reserved';
  for (const seg of storageForm.split('_')) {
    if (seg.length >= 3 && PROFANITY.has(seg)) return 'profanity';
  }
  return null;
}

export function isValidPublicHandle(storageForm: string): boolean {
  return publicHandleProblem(storageForm) === null;
}
